/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';

// Brand accent color
const ACCENT = '#7d6633';

const NotificationsDrawer = ({
  isOpen,
  onClose,
  items,
  unreadCount,
  loading,
  error,
  hasMore,
  load,
  markRead,
  markAllRead,
  reload,
  shellConfig = null
}) => {
  const { isDark } = useTheme();
  const { t, i18n } = useTranslation('admin');
  const navigate = useNavigate();
  const drawerRef = useRef(null);
  const [canPortal, setCanPortal] = useState(false);

  // Load notifications when drawer opens
  useEffect(() => {
    if (isOpen) {
      load({ limit: 20, offset: 0 });
    }
  }, [isOpen, load]);

  // Ensure we only access document on the client
  useEffect(() => {
    setCanPortal(true);
  }, []);

  // Handle ESC key to close drawer
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return t('admin.notif_just_now');
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return t('admin.notif_minutes_ago', { count: minutes });
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return t('admin.notif_hours_ago', { count: hours });
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return t('admin.notif_days_ago', { count: days });
    } else {
      return date.toLocaleDateString(i18n.language);
    }
  };

  // Handle notification item click
  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.read) {
      await markRead(notification.id);
    }
    
    // Keep drawer open as per requirements
  };

  // Handle mark all as read
  const handleMarkAllRead = async () => {
    if (unreadCount > 0) {
      await markAllRead();
    }
  };

  // Handle see all notifications
  const handleSeeAll = () => {
    navigate(shellConfig?.notificationsPath || '/admin/notifications');
    onClose();
  };

  // Load more notifications
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      load({ limit: 20, offset: items.length });
    }
  };

  if (!canPortal) {
    return null;
  }

  const drawerContent = (
    <>
      {/* Backdrop */}
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
        />
      )}
      
      {/* Drawer */}
      <motion.div
        ref={drawerRef}
        initial={{ x: '100%' }}
        animate={{ x: isOpen ? 0 : '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 h-full w-full max-w-md md:w-96 z-[9999] rounded-l-2xl shadow-2xl border-l border-white/20 dark:border-white/10 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl"
        style={{ 
          top: '96px', // Below the header
          height: 'calc(100vh - 96px)'
        }}
      >
        {/* Header */}
        <div className="p-4 md:p-5 border-b border-white/10 dark:border-white/10">
          {/* Row 1: Icon + Title + Close */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 truncate">{t('admin.notif_title')}</h3>
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="flex-shrink-0 p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
              aria-label="Close notifications"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </motion.button>
          </div>

          {/* Row 2: Unread count + Mark all read */}
          <div className="flex items-center justify-between gap-2 mt-2 pl-[52px]">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {unreadCount > 0 ? t('admin.notif_unread_count', { count: unreadCount }) : t('admin.notif_all_caught_up')}
            </p>
            {unreadCount > 0 && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleMarkAllRead}
                className="flex-shrink-0 px-3 py-1 text-xs font-medium rounded-xl transition-all duration-200 hover:shadow-lg text-white whitespace-nowrap"
                style={{ background: ACCENT }}
              >
                {t('admin.notif_mark_all_read')}
              </motion.button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col h-full">
          {/* Footer at Top */}
          <div className="flex-shrink-0 p-4 md:p-5 border-b border-white/10 dark:border-white/10">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSeeAll}
              className="w-full py-3 text-sm font-medium rounded-xl transition-all duration-200 hover:shadow-lg text-white flex items-center justify-center gap-2"
              style={{ background: ACCENT }}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {t('admin.notif_see_all')}
            </motion.button>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center p-8">
                <div className="flex items-center justify-center gap-3 text-zinc-600 dark:text-zinc-400">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-transparent border-t-current" style={{ color: ACCENT }}></div>
                  <span>{t('admin.notif_loading')}</span>
                </div>
              </div>
            ) : error ? (
              <div className="p-8 text-center">
                <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                  {t('admin.notif_failed_to_load')}
                </h3>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                  {t('admin.notif_load_error')}
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={reload}
                  className="px-4 py-2 rounded-xl text-white font-medium transition-all duration-200 hover:shadow-lg"
                  style={{ background: ACCENT }}
                >
                  {t('admin.try_again')}
                </motion.button>
              </div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center">
                <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                  <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                  {t('admin.notif_none_yet')}
                </h3>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                  {t('admin.notif_none_yet_subtitle')}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-white/10 dark:divide-white/10">
                {items.map((notification) => (
                  <motion.button
                    key={notification.id}
                    whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                    onClick={() => handleNotificationClick(notification)}
                    className="w-full text-left p-4 transition-colors duration-200 text-zinc-800 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30"
                  >
                    <div className="flex items-start gap-3">
                      {/* Status dot */}
                      {!notification.read && (
                        <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: ACCENT }} />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium mb-2 ${
                          !notification.read 
                            ? 'text-zinc-900 dark:text-zinc-100'
                            : 'text-zinc-700 dark:text-zinc-300'
                        }`}>
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-zinc-500 dark:text-zinc-400">
                            {formatRelativeTime(notification.created_at)}
                          </span>
                          
                          {notification.type && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              notification.type.startsWith('booking') ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                              notification.type.startsWith('client') ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
                              notification.type.startsWith('service') ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                              notification.type.startsWith('barber') ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' :
                              'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300'
                            }`}>
                              {notification.type.replace('.', ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.button>
                ))}
                
                {/* Load more button */}
                {hasMore && (
                  <div className="p-4 text-center border-t border-white/10 dark:border-white/10">
                    <motion.button
                      whileHover={{ scale: loading ? 1 : 1.05 }}
                      whileTap={{ scale: loading ? 1 : 0.95 }}
                      onClick={handleLoadMore}
                      disabled={loading}
                      className={`px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                        loading
                          ? 'text-zinc-400 cursor-not-allowed'
                          : 'text-white hover:shadow-lg'
                      }`}
                      style={loading ? {} : { background: ACCENT }}
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-transparent border-t-current" style={{ color: ACCENT }}></div>
                          {t('admin.loading')}
                        </div>
                      ) : (
                        t('admin.notif_load_more')
                      )}
                    </motion.button>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </motion.div>
    </>
  );

  return createPortal(drawerContent, document.body);
};

export default NotificationsDrawer;
